/**
 * Client of the **public prekey directory** (`apps/signaling`, endpoints
 * `/api/directory/*`). To write to an offline device, the sender
 * needs its public {@link PreKeyBundle}; the owner publishes it beforehand.
 *
 * Only PUBLIC material travels (public keys + signatures, all base64). The `fetch`
 * is injectable to test the wire format without network.
 */

import { base64ToBytes, bytesToBase64 } from "./crypto/encoding";
import type { DeviceIdentity, PreKeyBundle } from "./identity";
import type { FetchLike } from "./transport";

interface WireOneTimePreKey {
  readonly id: string;
  readonly pub: string;
}

/** Safe view of an external JSON as a record (without `as` over `unknown`). */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

/**
 * Serializes ALL the identity's one-time prekeys not yet published (marks
 * their ids as delivered) + the fixed material, to upload them to the directory. The
 * server hands them out one by one; the private key stays local and is consumed
 * when the first message that references it is received (X3DH responder).
 */
export function serializeBundleForDirectory(identity: DeviceIdentity): Record<string, unknown> {
  const oneTimePrekeys: WireOneTimePreKey[] = [];
  for (const [id, pair] of identity.oneTimePreKeys) {
    if (!identity.publishedOpkIds.has(id)) {
      oneTimePrekeys.push({ id, pub: bytesToBase64(pair.publicKey) });
      identity.publishedOpkIds.add(id);
    }
  }
  return {
    device: identity.deviceId,
    identityDhPub: bytesToBase64(identity.identityDh.publicKey),
    identitySigPub: bytesToBase64(identity.identitySig.publicKey),
    signedPrekeyId: identity.signedPreKey.keyId,
    signedPrekeyPub: bytesToBase64(identity.signedPreKey.keyPair.publicKey),
    signedPrekeySig: bytesToBase64(identity.signedPreKey.signature),
    oneTimePrekeys,
  };
}

/** Rebuilds a {@link PreKeyBundle} from the directory's `fetch` response. */
export function parseFetchedBundle(body: unknown): PreKeyBundle {
  const r = asRecord(body);
  if (
    typeof r.device !== "string" ||
    typeof r.identityDhPub !== "string" ||
    typeof r.identitySigPub !== "string" ||
    typeof r.signedPrekeyId !== "string" ||
    typeof r.signedPrekeyPub !== "string" ||
    typeof r.signedPrekeySig !== "string"
  ) {
    throw new TypeError("directory: invalid bundle shape");
  }
  let oneTimePreKeyId: string | undefined;
  let oneTimePreKeyPub: Uint8Array | undefined;
  if (typeof r.oneTimePrekey === "object" && r.oneTimePrekey !== null) {
    const o = asRecord(r.oneTimePrekey);
    if (typeof o.id === "string" && typeof o.pub === "string") {
      oneTimePreKeyId = o.id;
      oneTimePreKeyPub = base64ToBytes(o.pub);
    }
  }
  return {
    deviceId: r.device,
    identityDhPub: base64ToBytes(r.identityDhPub),
    identitySigPub: base64ToBytes(r.identitySigPub),
    signedPreKeyId: r.signedPrekeyId,
    signedPreKeyPub: base64ToBytes(r.signedPrekeyPub),
    signedPreKeySignature: base64ToBytes(r.signedPrekeySig),
    oneTimePreKeyId,
    oneTimePreKeyPub,
  };
}

export class DirectoryClient {
  constructor(
    private readonly baseUrl: string,
    private readonly space: string = "",
    private readonly fetchImpl: FetchLike = globalThis.fetch.bind(globalThis),
  ) {}

  private async json(path: string, init: RequestInit): Promise<unknown> {
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: { "content-type": "application/json", ...init.headers },
    });
    if (!res.ok) {
      throw new Error(`directory: ${res.status}`);
    }
    return res.json();
  }

  /** Publishes (or replaces by rotating/replenishing) this identity's public bundle. */
  async publish(identity: DeviceIdentity): Promise<void> {
    await this.json("/api/directory/publish", {
      method: "POST",
      body: JSON.stringify({ space: this.space, ...serializeBundleForDirectory(identity) }),
    });
  }

  /** Fetches a device's bundle consuming one OPK (or degraded to 3 DH). */
  async fetchBundle(deviceId: string): Promise<PreKeyBundle> {
    const q = this.space ? `?space=${encodeURIComponent(this.space)}` : "";
    const body = await this.json(`/api/directory/${encodeURIComponent(deviceId)}${q}`, {
      method: "GET",
    });
    return parseFetchedBundle(body);
  }

  /**
   * Publishes this device's identity→devices map (blob SIGNED by its
   * identity key; the server holds it opaquely, without interpreting it). The
   * peers use it for the live multi-device fan-out.
   */
  async publishLinks(deviceId: string, blob: string): Promise<void> {
    await this.json("/api/directory/links", {
      method: "POST",
      body: JSON.stringify({ space: this.space, device: deviceId, blob }),
    });
  }

  /** Fetches the device map published by `deviceId` (or undefined if none). */
  async fetchLinks(deviceId: string): Promise<string | undefined> {
    const q = this.space ? `?space=${encodeURIComponent(this.space)}` : "";
    const res = await this.fetchImpl(
      `${this.baseUrl}/api/directory/${encodeURIComponent(deviceId)}/links${q}`,
      { method: "GET", headers: { "content-type": "application/json" } },
    );
    if (res.status === 404) {
      return undefined;
    }
    if (!res.ok) {
      throw new Error(`directory: ${res.status}`);
    }
    const body = asRecord(await res.json());
    return typeof body.blob === "string" ? body.blob : undefined;
  }
}
